import {
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { RoleEnum, UserService, UserSummaryMacroItems } from '@libs/core/api';
import {
  debounceTime,
  distinctUntilChanged,
  first,
  from,
  map,
  merge,
  startWith,
  switchMap,
} from 'rxjs';
import { ModalController } from '@ionic/angular';
import {
  QuoteFiltersConfiguration,
  QuotesFiltersModalComponent,
} from '../quotes-filters-modal/quotes-filters-modal.component';
import { getValueOrError } from '@front-end/core/utils';
import { FormBuilder } from '@angular/forms';
import { ApiFiltersService, ProjectsService } from '@front-end/core/services';
import { LoadingService, ToastService } from '../../services';
import { tap } from 'rxjs/operators';
import { AnimationsService } from '../../services/animations.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

export enum DateOptionEnum {
  LAST_WEEK,
  LAST_MONTH,
  LAST_SEMESTER,
  ALL,
}

export enum QuoteStatusFilterEnum {
  Draft = 'Draft',
  WaitingForApproval = 'WaitingForPreApproval',
  Approved = 'Approved',
  OrderCreated = 'OrderCreated',
  Rejected = 'Rejected',
}

export interface QuoteFilters {
  toggle?: boolean;
  status?: (QuoteStatusFilterEnum | undefined)[];
  date?: (DateOptionEnum | undefined)[];
  caporeparti?: (UserSummaryMacroItems | undefined)[];
  description?: string;
}

type QuoteFiltersLike = {
  toggle?: boolean | null;
  status?: unknown[] | null;
  date?: unknown[] | null;
  caporeparti?: unknown[] | null;
  description?: string | null;
};

export const DEFAULT_QUOTE_FILTERS: QuoteFilters = {
  toggle: false,
  status: [undefined],
  date: [DateOptionEnum.LAST_WEEK],
  caporeparti: [undefined],
  description: '',
};

export const TEXT_ONLY_QUOTE_FILTERS: QuoteFilters = {
  description: '',
};

export const TEXT_ONLY_QUOTE_FILTERS_CONFIGURATION: QuoteFiltersConfiguration =
  {
    toggle: false,
    filters: false,
    status: false,
    date: false,
    caporeparti: false,
  };

@UntilDestroy()
@Component({
  selector: 'ui-quotes-filter',
  templateUrl: './quotes-filters.component.html',
  styleUrls: ['./quotes-filters.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuotesFiltersComponent {
  private _searchLoading = false;

  get hasActiveModalFilters(): boolean {
    const { status, date, caporeparti } = this.form.getRawValue();

    return (
      (this.configuration.status &&
        this.hasSelectedFilter(
          status as (QuoteStatusFilterEnum | undefined)[]
        )) ||
      (this.configuration.date &&
        this.hasSelectedFilter(date as (DateOptionEnum | undefined)[])) ||
      (this.configuration.caporeparti &&
        this.hasSelectedFilter(
          caporeparti as (UserSummaryMacroItems | undefined)[]
        ))
    );
  }

  @Input() set searchLoading(value: boolean) {
    this._searchLoading = value;
    this.changeDetectorRef.detectChanges();
  }
  get searchLoading(): boolean {
    return this._searchLoading;
  }
  @Input() toggleLabel!: string;
  @Input() caporeparti: UserSummaryMacroItems[] = [];
  @Input() set filters(filters: QuoteFilters) {
    const nextFilters = normalizeQuoteFilters(filters);

    this.searchControl.setValue(nextFilters.description ?? '', {
      emitEvent: false,
    });
    this.form.patchValue(
      {
        toggle: nextFilters.toggle,
        status: nextFilters.status,
        date: nextFilters.date,
        caporeparti: nextFilters.caporeparti,
      },
      { emitEvent: false }
    );
    this.changeDetectorRef.detectChanges();
  }
  @Input() configuration: QuoteFiltersConfiguration = {
    toggle: false,
    filters: true,
    status: true,
    date: true,
    caporeparti: true,
  };
  @Output() filtersChanged = new EventEmitter<QuoteFilters>();

  get shouldStackSearchBar(): boolean {
    return this.toggleLabel === 'quote.filters.showOnlyPreApproved';
  }

  readonly form = this.formBuilder.group({
    toggle: this.formBuilder.control<boolean>(false),
    status: this.formBuilder.control<(QuoteStatusFilterEnum | undefined)[]>([
      undefined,
    ]),
    date: this.formBuilder.control<(DateOptionEnum | undefined)[]>([
      DateOptionEnum.LAST_WEEK,
    ]),
    caporeparti: this.formBuilder.control<
      (UserSummaryMacroItems | undefined)[]
    >([undefined]),
  });
  readonly searchControl = this.formBuilder.control<string>('', {
    nonNullable: true,
  });

  readonly formUpdate$ = this.form.valueChanges.pipe(
    untilDestroyed(this),
    tap(() => this.emitCurrentFilters())
  );

  readonly viewUpdate$ = merge(
    this.formUpdate$,
    this.searchControl.valueChanges
  ).pipe(startWith(null));

  constructor(
    private modalController: ModalController,
    private userService: UserService,
    private loadingService: LoadingService,
    private toastService: ToastService,
    private formBuilder: FormBuilder,
    private changeDetectorRef: ChangeDetectorRef,
    private apiFiltersService: ApiFiltersService,
    private projectsService: ProjectsService,
    private animationService: AnimationsService
  ) {
    this.searchControl.valueChanges
      .pipe(
        untilDestroyed(this),
        tap(() => this.changeDetectorRef.detectChanges())
      )
      .subscribe();

    this.searchControl.valueChanges
      .pipe(
        untilDestroyed(this),
        debounceTime(300),
        distinctUntilChanged(),
        tap((description) => this.emitCurrentFilters(description ?? ''))
      )
      .subscribe();
  }

  clearSearch(): void {
    this.searchControl.setValue('');
    this.changeDetectorRef.detectChanges();
  }

  openFiltersModal(): void {
    this.projectsService.selectedProject$
      .pipe(
        first(),
        switchMap((project) =>
          this.userService.costCenterUserList({
            id: getValueOrError(project.id),
            $filter: this.apiFiltersService.getUserRoleFilter(
              RoleEnum.Caporeparto
            ),
            $myMacroItems: [RoleEnum.Operatore, RoleEnum.Caporeparto].includes(
              project.role as RoleEnum
            ),
          })
        ),
        this.loadingService.withLoading(),
        this.toastService.catchErrorWithToast(),
        switchMap((users) =>
          from(
            this.modalController.create({
              component: QuotesFiltersModalComponent,
              componentProps: {
                capoReparti: users,
                filters: normalizeQuoteFilters(this.form.getRawValue()),
                configuration: this.configuration,
              },
              enterAnimation: this.animationService.slideLeftToRight,
              leaveAnimation: this.animationService.slideRightToLeft,
            })
          )
        ),
        switchMap((modal) => from(modal.present()).pipe(map(() => modal))),
        switchMap((modal) => from(modal.onWillDismiss()))
      )
      .subscribe(({ data }) => {
        if (!data) {
          return;
        }

        this.form.patchValue(normalizeQuoteFilters(data), { emitEvent: false });
        this.emitCurrentFilters();
        this.changeDetectorRef.detectChanges();
      });
  }

  private emitCurrentFilters(description = this.searchControl.value): void {
    this.filtersChanged.emit(
      normalizeQuoteFilters({
        ...(this.form.getRawValue() as QuoteFilters),
        description,
      })
    );
  }

  private hasSelectedFilter(values?: unknown[]): boolean {
    return !!values?.length && !values.includes(undefined);
  }
}

export function normalizeQuoteFilters(
  filters?: QuoteFiltersLike
): QuoteFilters {
  return {
    toggle: filters?.toggle ?? DEFAULT_QUOTE_FILTERS.toggle ?? false,
    status: [
      ...((filters?.status as QuoteFilters['status']) ??
        (DEFAULT_QUOTE_FILTERS.status as QuoteFilters['status']) ?? [
          undefined,
        ]),
    ],
    date: [
      ...((filters?.date as QuoteFilters['date']) ??
        (DEFAULT_QUOTE_FILTERS.date as QuoteFilters['date']) ?? [
          DateOptionEnum.LAST_WEEK,
        ]),
    ],
    caporeparti: [
      ...((filters?.caporeparti as QuoteFilters['caporeparti']) ??
        (DEFAULT_QUOTE_FILTERS.caporeparti as QuoteFilters['caporeparti']) ?? [
          undefined,
        ]),
    ],
    description:
      filters?.description ?? DEFAULT_QUOTE_FILTERS.description ?? '',
  };
}
